import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Ticket, ArrowRight, Radar, Sparkles, Check, User, Smartphone, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { ensureAnonUser, setGpsLocal } from "@/lib/anonSession";
import InstallPrompt from "@/components/hola/InstallPrompt";
import CouponCard from "@/components/hola/CouponCard";
import PushOptIn from "@/components/hola/PushOptIn";
import { toast } from "sonner";

/**
 * /c/:merchantId — QR personalizado del comercio.
 * Une al usuario a la base de datos del comercio, entrega cupones de bienvenida,
 * activa el radar GPS y propone guardar la app (Cuponera) en el escritorio.
 */
export default function CouponJoinPage() {
  const { merchantId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null); // {merchant, coupons, subscribers}
  const [sub, setSub] = useState(null); // {is_new, welcome_delivered}
  const [name, setName] = useState("");
  const [nameSaved, setNameSaved] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pub, user] = await Promise.all([api.get(`/public/merchants/${merchantId}/coupons`), ensureAnonUser()]);
        setData(pub.data);
        setName(user.display_name || "");
        setGpsLocal(true);
        localStorage.setItem("holaradar_source_merchant", merchantId);
        const { data: s } = await api.post("/pwa/coupons/subscribe", { merchant_id: merchantId, device_id: user.device_id });
        setSub(s);
        const standalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
        if (!standalone) setTimeout(() => setShowInstall(true), 1200);
      } catch (e) {
        setError(true);
      } finally { setReady(true); }
    })();
  }, [merchantId]);

  const saveName = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      await api.put(`/users/anon/${localStorage.getItem("holaradar_device_id")}/profile`, { display_name: n });
      setNameSaved(true);
      toast.success(`¡Listo, ${n}! Tus cupones llegarán con tu nombre`);
    } catch { toast.error("No pude guardar tu nombre"); }
  };

  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0A0A0A] text-white">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#FF007A] grid place-items-center mx-auto mb-3 shadow-2xl shadow-[#FF007A]/40"><Ticket className="w-8 h-8 animate-pulse" /></div>
          <div className="font-heading font-extrabold">Preparando tus cupones…</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0A0A0A] text-white px-6">
        <div className="text-center max-w-sm">
          <div className="font-heading text-2xl font-extrabold">Comercio no encontrado</div>
          <p className="text-sm text-white/60 mt-2">El QR podría estar dañado o el comercio ya no participa.</p>
          <Button onClick={() => navigate("/scan")} className="mt-6 rounded-full bg-white text-black font-bold" data-testid="cjoin-fallback">Activar radar genérico</Button>
        </div>
      </div>
    );
  }

  const { merchant, coupons } = data;
  const welcome = coupons.filter((c) => c.welcome);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden">
      <div className="absolute -top-32 -right-24 w-80 h-80 rounded-full bg-[#FF007A]/30 blur-3xl" />
      <div className="absolute top-1/2 -left-32 w-72 h-72 rounded-full bg-[#0055FF]/30 blur-3xl" />

      <div className="relative max-w-md mx-auto px-6 pt-10 pb-16">
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          {merchant.logo_url ? (
            <img src={merchant.logo_url} alt="" className="w-20 h-20 rounded-2xl object-cover mx-auto mb-4 ring-4 ring-white/10" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-white/10 grid place-items-center mx-auto mb-4 ring-4 ring-white/10"><Gift className="w-9 h-9 text-[#FF007A]" /></div>
          )}
          <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 text-emerald-300 px-3 py-1 text-[10px] font-bold uppercase tracking-widest" data-testid="cjoin-status">
            <Check className="w-3 h-3" /> {sub?.is_new ? "¡Ya estás dentro!" : "¡Qué gusto verte de nuevo!"}
          </div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight mt-3 leading-tight" data-testid="cjoin-merchant-name">{merchant.business_name}</h1>
          <p className="text-white/70 text-sm mt-2 leading-relaxed">
            Ahora formas parte de la comunidad de <strong className="text-white">{merchant.business_name}</strong>. Recibirás cupones de descuento exclusivos directo en tu <strong className="text-[#FF007A]">CUPONERA</strong>.
          </p>
        </motion.div>

        {/* Cupones de bienvenida */}
        {welcome.length > 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-8">
            <div className="text-center text-[10px] uppercase tracking-widest font-bold text-[#FF007A] mb-3">
              <Sparkles className="w-3 h-3 inline mr-1 -mt-0.5" /> {sub?.welcome_delivered > 0 ? "Tu regalo de bienvenida ya está en tu Cuponera" : "Tu cupón de bienvenida"}
            </div>
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-6 px-6">
              {welcome.map((c) => (
                <div key={c.coupon_id} className="snap-center shrink-0 w-[78%]">
                  <CouponCard coupon={c} merchantName={merchant.business_name} holeColor="#0A0A0A" testid={`cjoin-coupon-${c.coupon_id}`} />
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
          <div className="mt-8 rounded-3xl bg-white/5 border border-white/10 p-5 text-center text-sm text-white/70" data-testid="cjoin-no-welcome">
            {merchant.business_name} te enviará cupones pronto. Ya quedaste en su lista.
          </div>
        )}

        {/* Nombre */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 rounded-3xl bg-white/5 border border-white/10 p-5">
          <div className="flex items-center gap-2 font-heading font-extrabold"><User className="w-4 h-4 text-[#FF007A]" /> ¿Cómo te llamas? <span className="text-white/40 text-xs font-semibold">(opcional)</span></div>
          <p className="text-xs text-white/60 mt-1">Así tus cupones llegan con tu nombre. Sin correo, sin teléfono, sin registro.</p>
          <div className="mt-3 flex gap-2">
            <Input value={name} onChange={(e) => { setName(e.target.value); setNameSaved(false); }} placeholder="Tu nombre" className="h-11 rounded-xl bg-white text-black border-0" maxLength={60} data-testid="cjoin-name-input" />
            <Button onClick={saveName} disabled={!name.trim() || nameSaved} className="h-11 rounded-xl bg-[#FF007A] hover:bg-[#e6006e] font-bold px-4" data-testid="cjoin-name-save">{nameSaved ? <Check className="w-4 h-4" /> : "Guardar"}</Button>
          </div>
        </motion.div>

        <div className="mt-4"><PushOptIn deviceId={localStorage.getItem("holaradar_device_id")} /></div>

        <Button onClick={() => navigate("/cuponera")} className="mt-8 h-14 w-full rounded-full bg-[#FF007A] hover:bg-[#e5006e] text-white font-bold text-base shadow-2xl shadow-[#FF007A]/40" data-testid="cjoin-go-cuponera">
          <Ticket className="w-5 h-5" /> Abrir mi CUPONERA <ArrowRight className="w-5 h-5" />
        </Button>
        <Button onClick={() => navigate("/radar")} variant="outline" className="mt-3 h-12 w-full rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 font-bold" data-testid="cjoin-go-radar">
          <Radar className="w-4 h-4" /> Ver ofertas cerca de mí (radar GPS)
        </Button>

        <div className="mt-6 flex items-center gap-2 justify-center text-[11px] text-white/50"><Smartphone className="w-3.5 h-3.5" /> Guarda la app en tu pantalla de inicio para tener tus cupones siempre a la mano</div>
      </div>

      {showInstall && <InstallPrompt variant="modal" onDismiss={() => setShowInstall(false)} />}
    </div>
  );
}
